import { cn } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import {
  Cube as CubeIcon,
  FileArrowDown as FileArrowDownIcon,
} from "@phosphor-icons/react";
import type { paths } from "@/lib/api/schema";

export type BimModelAuthor =
  paths["/api/yards/{yardId}/bim-models"]["get"]["responses"][200]["content"]["application/json"]["data"]["authors"][number];

type BimModelVersion =
  BimModelAuthor["lots"][number]["models"][number]["versions"][number];

export type MaquetteReceptionRow = {
  versionId: number;
  number: string | null;
  name: string;
  performances: string[];
  zones: string[];
  authorName: string;
  version: BimModelVersion;
  isProposed: boolean;
};

export function flattenMaquetteRows(
  authors: BimModelAuthor[],
  proposedOnly: boolean,
): MaquetteReceptionRow[] {
  const rows = new Map<number, MaquetteReceptionRow>();
  for (const author of authors) {
    for (const lot of author.lots) {
      for (const model of lot.models) {
        for (const version of model.versions) {
          const isProposed = version.status === "propose";
          if (proposedOnly && !isProposed) continue;
          const existing = rows.get(version.id);
          if (isNil(existing)) {
            rows.set(version.id, {
              versionId: version.id,
              number: model.number,
              name: model.name,
              performances: [...model.performances],
              zones: [...model.zones],
              authorName: author.name,
              version,
              isProposed,
            });
            continue;
          }
          existing.performances = [
            ...new Set([...existing.performances, ...model.performances]),
          ];
          existing.zones = [...new Set([...existing.zones, ...model.zones])];
        }
      }
    }
  }
  return Array.from(rows.values());
}

export function countProposedMaquetteVersions(
  authors: BimModelAuthor[],
): number {
  return flattenMaquetteRows(authors, true).length;
}

function formatDateEtat(version: BimModelVersion): string {
  if (isNil(version.dateEtat)) return version.index ?? "—";
  return new Date(version.dateEtat).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export type MaquetteFormatButtonsProps = {
  fichierIfcId: number | null;
  fichierNativeId: number | null;
  ifcTitle: string;
  nativeTitle: string;
  onDownload: (fichierId: number) => void;
  /** Replaces the download of the IFC picto (e.g. opening the viewer). */
  onOpenIfc?: (fichierIfcId: number) => void;
};

export function MaquetteFormatButtons({
  fichierIfcId,
  fichierNativeId,
  ifcTitle,
  nativeTitle,
  onDownload,
  onOpenIfc = onDownload,
}: MaquetteFormatButtonsProps) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {isNil(fichierIfcId) ? (
        <span className="w-[18px]" />
      ) : (
        <button
          type="button"
          onClick={() => onOpenIfc(fichierIfcId)}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
          title={ifcTitle}
        >
          <CubeIcon size={18} />
        </button>
      )}
      {isNil(fichierNativeId) ? (
        <span className="w-[18px]" />
      ) : (
        <button
          type="button"
          onClick={() => onDownload(fichierNativeId)}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
          title={nativeTitle}
        >
          <FileArrowDownIcon size={18} />
        </button>
      )}
    </div>
  );
}

export type MaquettesReceptionTableProps = {
  rows: MaquetteReceptionRow[];
  proposedOnly: boolean;
  onDownload: (fichierId: number) => void;
};

export function MaquettesReceptionTable({
  rows,
  proposedOnly,
  onDownload,
}: MaquettesReceptionTableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-zinc-500 dark:text-zinc-400">
          {proposedOnly
            ? "Aucune proposition à étudier"
            : "Aucune maquette à afficher"}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col style={{ width: "calc((100% - 23rem) * 12 / 110)" }} />
          <col style={{ width: "calc((100% - 23rem) * 30 / 110)" }} />
          <col style={{ width: "calc((100% - 23rem) * 24 / 110)" }} />
          <col style={{ width: "calc((100% - 23rem) * 19 / 110)" }} />
          <col style={{ width: "calc((100% - 23rem) * 25 / 110)" }} />
          <col className="w-36" />
          <col className="w-32" />
          <col className="w-24" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-800">
          <tr className="h-10 border-b border-zinc-200 dark:border-zinc-700">
            <Th>N°</Th>
            <Th>Intitulé</Th>
            <Th>Prestation</Th>
            <Th>Niveaux / Façades</Th>
            <Th>Auteur</Th>
            <Th>Date d'état</Th>
            <Th>Date de dépôt</Th>
            <Th>Formats</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const maquette = `${row.number ?? "—"} ${row.name}`;
            return (
              <tr
                key={row.versionId}
                className={cn(
                  "h-10 border-b border-zinc-100 dark:border-zinc-700/50",
                  row.isProposed
                    ? "bg-amber-50 dark:bg-amber-950/30"
                    : "hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20",
                )}
              >
                <Td
                  value={row.number ?? "—"}
                  className={cn(
                    "font-mono text-xs",
                    row.isProposed
                      ? "font-semibold text-amber-700 dark:text-amber-400"
                      : "text-zinc-900 dark:text-zinc-100",
                  )}
                />
                <Td value={row.name} />
                <Td value={row.performances.join(", ") || "—"} />
                <Td value={row.zones.join(", ") || "—"} />
                <Td value={row.authorName} />
                <Td value={formatDateEtat(row.version)} />
                <Td
                  value={new Date(row.version.createdAt).toLocaleDateString(
                    "fr-FR",
                  )}
                />
                <td className="px-2">
                  <MaquetteFormatButtons
                    fichierIfcId={row.version.fichierIfcId}
                    fichierNativeId={row.version.fichierNativeId}
                    ifcTitle={`Télécharger le fichier IFC de la maquette ${maquette}`}
                    nativeTitle={`Télécharger le fichier natif de la maquette ${maquette}`}
                    onDownload={onDownload}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 text-center font-medium text-zinc-700 dark:text-zinc-300">
      {children}
    </th>
  );
}

function Td({ value, className }: { value: string; className?: string }) {
  return (
    <td className="px-3 text-center text-zinc-700 dark:text-zinc-300">
      <div className={cn("truncate", className)} title={value}>
        {value}
      </div>
    </td>
  );
}
